import { z } from 'zod';
import { Email } from './auth.js';
import { DisplayName, Slug, TeamRole, Ulid, UserIdentity } from './common.js';
/** Teams, membership and invites (plan §3, §5). */
export const CreateTeamRequest = z.object({
    name: DisplayName,
    /** Derived from `name` when omitted; must be globally unique. */
    slug: Slug.optional(),
});
/** Both optional: a rename, a re-slug, or either on its own. The slug is display-only —
 *  nothing resolves a team by it — so changing it breaks no link. */
export const UpdateTeamRequest = z.object({
    name: DisplayName.optional(),
    slug: Slug.optional(),
});
export const Team = z.object({
    id: Ulid,
    slug: z.string(),
    name: z.string(),
    createdAt: z.number().int(),
    /** The requesting user's role in this team. */
    role: TeamRole,
    /**
     * The feature keys the requesting user has **in this team** (`features.ts`).
     *
     * Enabled keys only, and never the plan that produced them. A disabled feature is absent, so
     * a response to a free account is shaped exactly like one from a deployment where the feature
     * was never built — which is the whole reason plans are invisible. Clients test membership
     * and must not treat an absent key as an error.
     *
     * Optional on the wire so an older client, or a response from an API surface that has not
     * resolved them, is not a parse failure. Absent means "this client was not told"; it does not
     * mean "none".
     */
    features: z.array(z.string()).optional(),
});
/**
 * One person on the roster.
 *
 * Spreads `UserIdentity` like every other list that names people, and is the one list where the
 * name and the address are always filled in: this endpoint returns the members of a team to a
 * member of that team, so the rule in `apps/api/src/lib/identity.ts` says yes to every row by
 * construction. `displayName` is still nullable, because giving a real name is optional.
 */
export const TeamMember = z.object({
    ...UserIdentity.shape,
    role: TeamRole,
    joinedAt: z.number().int(),
    /**
     * Whether this member gets an email for each feedback report (`docs/feedback-plan.md` §7.4) —
     * the answer, not the stored choice: an owner or admin nobody has switched off is `true`, and a
     * tester is always `false`. Whether a confirmed address is behind it is not said here.
     */
    feedbackEmail: z.boolean(),
});
export const UpdateMemberRequest = z.object({
    role: TeamRole,
});
/**
 * `PUT /v1/teams/:teamId/members/:userId/feedback-email` — one member's switch. An owner or admin
 * sets anybody's; every owner, admin and developer sets their own. A tester has no switch.
 */
export const UpdateFeedbackEmailRequest = z.object({
    on: z.boolean(),
});
export const CreateInviteRequest = z.object({
    email: Email,
    role: TeamRole.default('tester'),
    /** Days until the invite link stops working. */
    expiresInDays: z.number().int().min(1).max(90).default(14),
});
export const Invite = z.object({
    id: Ulid,
    teamId: Ulid,
    teamName: z.string(),
    email: z.string(),
    role: TeamRole,
    expiresAt: z.number().int(),
    acceptedAt: z.number().int().nullable(),
    createdAt: z.number().int(),
});
/**
 * The invite token is returned exactly once, at creation, because only its hash is stored.
 * Delivering it (email, chat, carrier pigeon) is the caller's problem — there is no mail
 * sender in this system.
 */
export const CreateInviteResponse = z.object({
    invite: Invite,
    token: z.string(),
    acceptUrl: z.string(),
});
/* --------------------------------------------------------------------- package prefixes */
export const ClaimPrefixRequest = z.object({
    /** Not `PackageName`: normalization and the full §4 claim check happen server-side so
     *  the client gets one authoritative answer instead of two subtly different ones. */
    prefix: z.string().trim().min(1).max(255),
});
export const PackagePrefix = z.object({
    id: Ulid,
    teamId: Ulid,
    prefix: z.string(),
    /** Reserved for DNS TXT proof (plan §4); always null in v1. */
    verifiedAt: z.number().int().nullable(),
    createdAt: z.number().int(),
});
/* --------------------------------------------------------------- MCP keys the team holds */
/**
 * The most a key is allowed to be — the same bound agentd puts on every value in `mcp.json`, so a
 * key the Worker accepts is never one the sandbox then refuses.
 */
export const MAX_TEAM_MCP_KEY_CHARS = 4096;
/**
 * `PUT /v1/teams/:teamId/mcp-keys/:provider` — an admin giving the team a key for one catalogue
 * entry (`@boardflight/shared/mcp-catalog`, `teamKeyEntry`).
 *
 * Trimmed, because a key pasted with the newline after it is the same key. Anything else that is
 * not printable is refused rather than cleaned: the key goes out in an HTTP header or an
 * environment variable, and a control character in either is a different key, or no request.
 */
export const SetTeamMcpKeyRequest = z.object({
    key: z
        .string()
        .trim()
        .min(1, 'Paste the key.')
        .max(MAX_TEAM_MCP_KEY_CHARS)
        // eslint-disable-next-line no-control-regex
        .refine((key) => !/[\x00-\x1f\x7f]/.test(key), 'A key cannot contain line breaks or control characters.'),
});
/**
 * One key the team holds, as anyone on the team may see it: **that** it is held, by whom and since
 * when — never the key, nor any part of it. Not even to the admin who set it, who already has it.
 *
 * `setBy` is one string decided by `nameFor` (`packages/worker-core/src/identity.ts`), because the
 * admin who set a key may since have left the team and is then a username like anyone else.
 */
export const TeamMcpKey = z.object({
    /** The catalogue id: `ludo`, `replicate`. */
    provider: z.string(),
    setBy: z.string().nullable(),
    setAt: z.number().int(),
});
//# sourceMappingURL=teams.js.map